/**
 * Report templates for quick creation
 */

export const reportTemplates = [
  {
    id: 'tech-ai-weekly',
    name: 'Tech & AI Weekly',
    reportName: 'Weekly Brief: Technology & AI',
    prompt: `# Weekly Brief: Technology & AI

## Mission
Research and analyze the most significant technology and AI developments from the past week for tech-savvy professionals and enthusiasts.

## What to Research

### AI & Machine Learning
- Model releases and updates (OpenAI, Anthropic, Google, Meta, etc.)
- Research breakthroughs, benchmark achievements
- AI safety, ethics, regulation debates
- Enterprise AI adoption, use cases
- Open source developments

### Big Tech & Platforms
- Product launches, feature updates
- Company earnings, strategic shifts
- Leadership changes, organizational restructuring
- Antitrust, regulation, policy battles
- Major platforms: Apple, Google, Microsoft, Meta, Amazon, Tesla

### Emerging Tech
- Quantum computing, robotics, biotech
- AR/VR/spatial computing
- Semiconductors, hardware innovation
- Clean tech, space tech

### Cybersecurity & Infrastructure
- Major breaches, vulnerabilities
- Security tools, threat intelligence
- Cloud infrastructure developments
- Privacy regulations, data protection

### Developer & Tools
- Programming languages, frameworks
- DevOps, infrastructure tools
- Notable open source projects
- Developer platform updates (GitHub, GitLab, etc.)

## Key Sources
**Tech Media**: TechCrunch, The Verge, Ars Technica, Wired, The Information, Protocol
**AI-Specific**: MIT Technology Review, VentureBeat AI, Papers with Code, arXiv highlights
**Business**: Bloomberg Tech, Financial Times Tech, Wall Street Journal
**Company**: Official blogs, research papers, developer documentation
**Community**: Hacker News, Reddit (r/technology, r/MachineLearning), X/Twitter tech discourse

## Deliverable

**Executive Summary** (150-200 words): Top 3-4 breakthroughs or shifts, emerging trends, key takeaway

**Featured Developments** (4-6): What launched/happened, technical details, why it matters, industry impact

**Quick Hits** (6-8 bullets): Other notable releases, updates, or news worth tracking

**Developer Corner**: Tools, libraries, or resources launched this week worth noting

**Looking Ahead**: Expected releases, conferences, or developments in coming week

## Standards
- Balance consumer tech, enterprise, and developer perspectives
- Explain technical concepts clearly without oversimplifying
- Distinguish hype from substance
- Include performance metrics, benchmarks where relevant
- Link to technical documentation, papers, demos

**Target**: 1,200-1,800 words | **Tone**: Tech-savvy but accessible`
  },
  {
    id: 'business-startups-weekly',
    name: 'Business & Startups Weekly',
    reportName: 'Weekly Brief: Business & Startups',
    prompt: `# Weekly Brief: Business & Startups

## Mission
Research and analyze the most significant business developments and startup ecosystem news from the past week for entrepreneurs, investors, and business professionals.

## What to Research

### Startup Ecosystem
- Major funding rounds (Series A and above)
- New unicorns, down rounds, valuations
- Notable launches and pivots
- Startup failures and shutdowns
- Accelerator/incubator cohorts

### Venture Capital & Investment
- VC firm announcements, new funds
- Investment trends by sector and stage
- Notable investor moves, syndicate formation
- Exit activity: IPOs, acquisitions, secondaries

### Business Strategy & Innovation
- Major corporate strategy shifts
- Business model innovations
- Industry disruption stories
- Competitive dynamics, market share battles
- Partnership and collaboration announcements

### Key Industries
- Fintech, SaaS, E-commerce, Healthcare/Biotech
- Climate tech, AI companies (business angle)
- Consumer tech, B2B platforms
- Emerging categories gaining traction

### Entrepreneurship & Leadership
- Founder stories, lessons learned
- Leadership changes at high-growth companies
- Company culture developments
- Talent market trends, hiring/layoffs

### Market Dynamics
- Economic factors affecting startups
- Regulatory changes impacting business
- Market corrections or opportunities
- Geographic trends (Silicon Valley, Europe, Asia, emerging hubs)

## Key Sources
**Startup Media**: TechCrunch, The Information, Sifted (Europe), Crunchbase News
**Business**: Bloomberg, Financial Times, Wall Street Journal, Forbes, Inc., Fast Company
**VC/Investor**: PitchBook, CB Insights, VC blogs and newsletters
**Regional**: Breakit (Nordics), TechEU, Tech in Asia
**Data**: Crunchbase, PitchBook, Tracxn, company announcements

## Deliverable

**Executive Summary** (150-200 words): Top 3-4 stories, market sentiment, key insight for founders/investors

**Major Developments** (4-6): What happened, key players/companies, why it matters, industry implications

**Funding Highlights**: Notable rounds with amounts, investors, and what companies do (table or bullets)

**Trend Watch**: Emerging patterns across sectors, geographies, or business models

**Ecosystem Notes** (4-6 bullets): Other launches, pivots, acquisitions, or notable news

**What It Means**: Brief analysis of what these developments signal for entrepreneurs and investors

## Standards
- Include specific numbers: funding amounts, valuations, metrics
- Provide business context and competitive landscape
- Balance optimism with realistic assessment
- Cite credible sources and data platforms
- Highlight lessons or insights for entrepreneurs

**Target**: 1,200-1,800 words | **Tone**: Professional but entrepreneurial`
  },
  {
    id: 'swedish-finance-weekly',
    name: 'Swedish Finance Weekly',
    reportName: 'Deep Research: Swedish Financial Space',
    prompt: `# Deep Research: Swedish Financial Space - Weekly Analysis

## Mission
Research the Swedish financial sector for the past week and analyze key market movements, corporate developments, and emerging trends.

## What to Research

### Market & Corporate
- OMXS30 performance and sector trends
- Major companies: Volvo, Ericsson, H&M, Investor AB, Atlas Copco, Nordea, SEB, Handelsbanken
- Earnings reports, M&A activity, strategic announcements
- Key sectors: Financial services, Industrial, Technology, Real estate, Green energy

### Economic & Regulatory
- Riksbank policy updates
- Key SCB releases: GDP, inflation, employment, housing
- SEK currency movements vs EUR/USD
- Finansinspektionen regulations or EU regulatory impacts
- Fintech developments (Klarna, Swish, digital banking)

## Key Sources
**Swedish**: Dagens Industri, Svenska Dagbladet, SCB, Riksbank, Finansinspektionen
**International**: Financial Times, Bloomberg, Reuters
**Corporate**: Company press releases, quarterly reports

## Deliverable

**Executive Summary** (200-300 words): Top 3-5 developments, market sentiment, key takeaway

**Detailed Analysis**: For each major development, explain what happened (dates, numbers), why it matters, and implications

**Market Outlook**: Sentiment indicators, analyst views, risk factors, upcoming events

## Standards
- Include specific numbers and data points
- Cite sources for major claims
- Provide context (week-over-week, year-over-year)
- Go beyond news reporting—analyze implications

**Target**: 1,500-2,500 words | **Audience**: Professional investors`
  },
  {
    id: 'eu-international-weekly',
    name: 'EU & International Weekly',
    reportName: 'Weekly Brief: EU & International News',
    prompt: `# Weekly Brief: EU & International News

## Mission
Research and analyze the most significant EU and international developments from the past week for educated global news readers.

## What to Research

### European Union
- EU policy, legislation, and economic affairs (ECB, trade, budget)
- Politics, elections, member state dynamics
- Foreign policy, migration, climate, defense, digital regulation

### Major Regions & Powers
- **US**: Politics, policy, international actions
- **China**: Economic developments, geopolitical moves
- **Russia & Eastern Europe**: Conflicts, politics, regional dynamics
- **Middle East**: Israel/Palestine, Iran, Gulf states, Turkey
- **Asia-Pacific**: Japan, Korea, India, ASEAN
- **Africa & Latin America**: Major political/economic stories

### Global Issues
- Security & conflicts, climate & environment
- Global economy, technology governance
- UN, NATO, WTO, IMF, WHO actions

## Key Sources
**European**: Politico Europe, Euractiv, The Guardian, Le Monde, Financial Times
**International**: BBC, Reuters, AP, Al Jazeera, The Economist, NYT, WSJ
**Official**: EU institutions, UN, national governments, think tanks

## Deliverable

**Executive Summary** (150-200 words): Top 3-4 stories, emerging patterns, key takeaway

**Featured Stories** (4-6): What happened, key players, why it matters, analysis

**Regional Roundup**: 2-3 notable stories from Europe, Americas, Asia-Pacific, Middle East & Africa

**The Week Ahead**: Scheduled events, ongoing situations, potential flashpoints

## Standards
- Present multiple viewpoints, avoid single-country bias
- Explain historical context and significance
- Highlight how regional events interconnect
- Make complex geopolitics accessible without oversimplifying

**Target**: 1,200-1,800 words | **Tone**: Authoritative yet accessible`
  },
  {
    id: 'swedish-news-weekly',
    name: 'Swedish News Weekly',
    reportName: 'Weekly Brief: Swedish News & International Context',
    prompt: `# Weekly Brief: Swedish News & International Context

## Mission
Research and analyze the most significant Swedish news and related international developments from the past week for educated news readers.

## What to Research

### Domestic Sweden
- Politics & governance, social trends, cultural events
- Environment & climate, crime & justice
- Healthcare & education, innovation & business

### Sweden in the World
- Nordic relations, EU affairs, NATO & defense
- International trade, Swedish global impact

### International News Affecting Sweden
- Geopolitical events affecting Scandinavia
- EU developments with Swedish implications
- Global trends impacting Swedish society

## Key Sources
**Swedish**: DN, SvD, SVT, Expressen, Sveriges Radio, Göteborgs-Posten
**International**: The Local Sweden, Reuters, BBC, Politico Europe

## Deliverable

**Executive Summary** (150-200 words): Top 3-4 stories, overall theme, key takeaway

**Main Stories** (3-5): What happened, context, why it matters, what's next

**Briefly Noted** (5-8 bullets): Other noteworthy items, mix domestic and international

**Looking Ahead**: Key events expected in coming week

## Standards
- Write for intelligent general readers, not specialists
- Provide context so readers understand significance
- Balance politics, society, culture, and international news
- Include Swedish perspectives and domestic debate

**Target**: 1,000-1,500 words | **Tone**: Informed but accessible`
  }
]
